import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ObjectId } from 'bson';
import { addToDate } from '@hero/core';
import { agreementCreateAPISchema } from '@hero/api';
import { getObjectId } from '@hero/schema';
import type { Agreement, Client, PartyRole } from '@hero/schema';
import { fileToBase64 } from '@hero/web-utils';

/**
 * Agreement form schema — `agreementCreateAPISchema` plus two
 * tightenings the form layer enforces beyond the API:
 *
 *  - `title` is required + trimmed-non-empty. The API allows
 *    `undefined` and falls back to a synthesized
 *    "Acme × Hero — 2026" label, but a human-typed title is
 *    significantly more useful in lists / drawers, so we surface
 *    it as a hard-required form field. Submitting a non-empty
 *    string is a strict subset of what the API accepts.
 *  - `dates.end >= dates.start` cross-field check. The model
 *    requires both dates but doesn't constrain ordering server-side;
 *    catching this at the form layer means the user sees the issue
 *    inline rather than as a 4xx after Save.
 */
const agreementFormSchema = agreementCreateAPISchema
  .extend({
    title: z.string().trim().min(1),
  })
  .superRefine((data, ctx) => {
    if (data.dates && data.dates.end.getTime() < data.dates.start.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'end_before_start',
        path: ['dates', 'end'],
      });
    }
  });

export type AgreementFormValues = z.infer<typeof agreementFormSchema>;

/**
 * Default lifecycle window when nothing is pre-filled —
 * `today → today + 1 year`. `Agreement.dates` is required at the
 * schema layer (relations-v2), so the form needs a complete starting
 * window so Save isn't disabled on first open. The user can override
 * either input.
 */
const DEFAULT_DURATION_DAYS = 365;

/**
 * Default counterparty roles when the picker doesn't surface roles
 * (e.g. legacy `Client` docs predating the `roles[]` field). A fresh
 * Client created from inside the agreement modal will carry its own
 * `roles[]` and override this default.
 */
const DEFAULT_COUNTERPARTY_ROLES: PartyRole[] = ['business_customer'];

/** Calendar-month / weekly cadence — the most common contract pattern in the network. */
const DEFAULT_PAYMENT_TERMS = {
  invoiceIssuance: { type: 'month', value: 1 },
  timeToPay: { type: 'week', value: 1 },
} as const;

export interface UseAgreementFormOptions {
  /**
   * Initial counterparty + title hints (e.g. opening the modal from
   * a Client workspace pre-fills `counterpartyId` so the user doesn't
   * have to re-select). Overwritten when an existing agreement is
   * loaded via {@link useAgreementForm}'s `setAgreement`.
   */
  defaultValues?: Partial<AgreementFormValues>;
  /**
   * Org id of the current user — required to build the default form
   * state (`orgId` is part of the create payload). Pulled from
   * `useCurrentUser()` by the manager hook and threaded down.
   */
  orgId?: string;
  /**
   * Called with the validated form values on a successful submit.
   * The manager hook supplies this; consumers that use
   * `useAgreementForm` directly can ignore it and call
   * `form.handleSubmit(...)` themselves.
   */
  onSubmit?: (values: AgreementFormValues) => void;
}

/** Build the create-mode default form values. */
function getDefaultValues(
  orgId: string | undefined,
  overrides?: Partial<AgreementFormValues>,
): Partial<AgreementFormValues> {
  const start = new Date();
  const end = addToDate(DEFAULT_DURATION_DAYS, 'day', start).date;
  return {
    orgId: (orgId ? getObjectId(orgId) : undefined) as ObjectId,
    title: '',
    type: 'general_contract_and_quotation',
    // Default to `active` rather than `new`. Agreements created from
    // the dashboard are typically already-signed contracts the user
    // is logging into the platform; `new` is reserved for the rarer
    // "drafted, awaiting counterparty approval" flow and the user
    // can switch to it explicitly via the status field if needed.
    status: 'active',
    monthlyVehicleVolume: 0,
    avgDailyVehicleVolume: 0,
    dates: { start, end, autoRenew: true },
    terms: {
      payment: { ...DEFAULT_PAYMENT_TERMS },
      itemsInsuranceCoveragePerVehicle: 0,
    },
    UTags: [],
    ...overrides,
  } as Partial<AgreementFormValues>;
}

/**
 * Convert an existing Agreement into the form-values shape. The
 * counterparty side is whichever party in `parties[]` doesn't match
 * the current `orgId`; we fall back to `parties[1]` when the org
 * can't be identified (legacy / single-org docs).
 */
function agreementToFormValues(
  agreement: Agreement,
  orgId: string | undefined,
): AgreementFormValues {
  const [a, b] = agreement.parties;
  const orgPartyMatches = (id: ObjectId) => orgId && id.toString() === orgId;
  const counterpartyParty = orgPartyMatches(a._id) ? b : orgPartyMatches(b._id) ? a : b;

  return {
    orgId: agreement.org._id,
    title: agreement.title,
    type: agreement.type,
    status: agreement.status,
    monthlyVehicleVolume: agreement.monthlyVehicleVolume,
    avgDailyVehicleVolume: agreement.avgDailyVehicleVolume,
    dates: {
      start: agreement.dates.start,
      end: agreement.dates.end,
      autoRenew: agreement.dates.autoRenew,
    },
    terms: agreement.terms,
    contractPDF: agreement.contractPDF,
    quotationPDF: agreement.quotationPDF,
    UTags: agreement.UTags ?? [],
    counterparty: {
      _id: counterpartyParty._id,
      target: counterpartyParty.target,
      roles: counterpartyParty.roles,
      name: counterpartyParty.name,
    },
  } as AgreementFormValues;
}

/**
 * `useAgreementForm` — Form-state target hook for create / edit
 * agreement flows. Pairs with {@link useAgreementEditManager} which
 * orchestrates this hook against the API hooks layer; consumers of
 * the modal don't call this directly.
 *
 * Validation comes from {@link agreementFormSchema} via
 * `zodResolver`, so `formState.isValid` accurately reflects whether
 * the captured values can be sent to `agreementCreate` /
 * `agreementUpdate` without a Zod parse failure.
 *
 * Field handlers are exposed for inputs that aren't natively driven
 * by `<CustomFormField>` — `ClientSelector`, `DateField`,
 * `PaymentTermsFields`, and `FileUploader` — so the form component
 * stays declarative and doesn't reach into `form.setValue` directly.
 */
export function useAgreementForm(options?: UseAgreementFormOptions) {
  const { onSubmit, orgId, defaultValues: defaultValuesProp } = options ?? {};

  /**
   * Tracks whether the user has explicitly picked an end date. Until
   * they do, changing `dates.start` re-anchors `dates.end = start +
   * 1y` so the window stays a one-year contract by default. After
   * the user picks an explicit `end`, we freeze it so subsequent
   * `start` edits don't stomp on their choice.
   */
  const [endsAtTouched, setEndsAtTouched] = useState(false);

  /**
   * Local mirrors of the dropped `File` objects for preview only —
   * `FileUploader` reads `file.name` / `file.size` / a blob-URL
   * thumbnail off this array. The form-state holds the canonical
   * `{ upload: base64 }` shape we send to the API; storing the raw
   * `File` in form state would force it through the Zod resolver
   * (which doesn't know about browser `File` instances) and break
   * `formState.isValid`. Two slots, indexed by side.
   */
  const [contractPDFFiles, setContractPDFFiles] = useState<File[]>([]);
  const [quotationPDFFiles, setQuotationPDFFiles] = useState<File[]>([]);

  const defaultValues = useMemo(
    () => getDefaultValues(orgId, defaultValuesProp),
    [orgId, defaultValuesProp],
  );

  const form = useForm<AgreementFormValues>({
    resolver: zodResolver(agreementFormSchema),
    defaultValues: defaultValues as AgreementFormValues,
    mode: 'onChange',
  });

  const setAgreement = useCallback(
    (agreement: Agreement) => {
      form.reset(agreementToFormValues(agreement, orgId));
      // Edit-mode docs already have a final end-date; mark as
      // touched so a `start` change doesn't auto-reset it.
      setEndsAtTouched(true);
      // Clear local file mirrors — the loaded agreement's PDFs are
      // shown via `existingUrls` (read off `agreement.contractPDF.url`)
      // instead of a freshly-dropped `File`.
      setContractPDFFiles([]);
      setQuotationPDFFiles([]);
    },
    [form, orgId],
  );

  const reset = useCallback(() => {
    form.reset(getDefaultValues(orgId, defaultValuesProp) as AgreementFormValues);
    setEndsAtTouched(false);
    setContractPDFFiles([]);
    setQuotationPDFFiles([]);
  }, [form, orgId, defaultValuesProp]);

  // ─── Field handlers (non-`CustomFormField` inputs) ─────────────

  const handleStartChange = useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      form.setValue('dates.start', date, { shouldValidate: true, shouldDirty: true });
      if (!endsAtTouched) {
        const nextEnd = addToDate(DEFAULT_DURATION_DAYS, 'day', date).date;
        form.setValue('dates.end', nextEnd, { shouldValidate: true, shouldDirty: true });
      }
    },
    [form, endsAtTouched],
  );

  const handleEndChange = useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      form.setValue('dates.end', date, { shouldValidate: true, shouldDirty: true });
      setEndsAtTouched(true);
    },
    [form],
  );

  const handleCounterpartySelect = useCallback(
    (clients: Client[] | undefined) => {
      const client = clients?.[0];
      if (!client) {
        form.setValue('counterparty', undefined as never, {
          shouldValidate: true,
          shouldDirty: true,
        });
        return;
      }
      const roles =
        Array.isArray(client.roles) && client.roles.length > 0
          ? (client.roles as PartyRole[])
          : DEFAULT_COUNTERPARTY_ROLES;
      form.setValue(
        'counterparty',
        {
          _id: client._id,
          target: 'client',
          roles,
          name: client.name,
        },
        { shouldValidate: true, shouldDirty: true },
      );
    },
    [form],
  );

  const handleTermsChange = useCallback(
    (next: AgreementFormValues['terms']) => {
      form.setValue('terms', next, { shouldValidate: true, shouldDirty: true });
    },
    [form],
  );

  const handleContractPDFChange = useCallback(
    async (files: File[]) => {
      setContractPDFFiles(files);
      const file = files[0];
      if (!file) {
        form.setValue('contractPDF', undefined, { shouldValidate: true, shouldDirty: true });
        return;
      }
      const upload = await fileToBase64(file);
      form.setValue(
        'contractPDF',
        { upload },
        { shouldValidate: true, shouldDirty: true },
      );
    },
    [form],
  );

  const handleQuotationPDFChange = useCallback(
    async (files: File[]) => {
      setQuotationPDFFiles(files);
      const file = files[0];
      if (!file) {
        form.setValue('quotationPDF', undefined, { shouldValidate: true, shouldDirty: true });
        return;
      }
      const upload = await fileToBase64(file);
      form.setValue(
        'quotationPDF',
        { upload },
        { shouldValidate: true, shouldDirty: true },
      );
    },
    [form],
  );

  const onValidSubmit = (values: AgreementFormValues) => {
    onSubmit?.(values);
  };

  return {
    form,
    /** Reactive snapshot of the current form values. */
    values: form.watch(),
    setAgreement,
    reset,
    onValidSubmit,
    /** Whether the form passes Zod validation — drives Save enable/disable. */
    isValid: form.formState.isValid,
    handleStartChange,
    handleEndChange,
    handleCounterpartySelect,
    handleTermsChange,
    handleContractPDFChange,
    handleQuotationPDFChange,
    /** File mirrors for `<FileUploader files={...}>` previews. */
    contractPDFFiles,
    quotationPDFFiles,
  };
}
